import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="container">
        <header className="header">
          <h1>Web Time Insight</h1>
          <p className="subtitle">Privacy Policy</p>
        </header>

        <div className="content">
          <section className="section">
            <h2>Last Updated: January 2026</h2>
            <p>
              This Privacy Policy describes how Web Time Insight ("we", "our", or "the extension") 
              handles your data. We are committed to protecting your privacy and ensuring transparency 
              about our data practices.
            </p>
          </section>

          <section className="section">
            <h2>1. Data Collection and Storage</h2>
            <p>
              <strong>Web Time Insight collects and stores the following data locally on your device:</strong>
            </p>
            <ul>
              <li>
                <strong>Browsing Time Data:</strong> The extension tracks the amount of time you spend 
                on different websites and web pages. This includes:
                <ul>
                  <li>Domain names (e.g., youtube.com, github.com)</li>
                  <li>Full URLs (optional, for detailed tracking)</li>
                  <li>Time spent per domain/URL per day</li>
                </ul>
              </li>
              <li>
                <strong>Storage Location:</strong> All data is stored locally on your device using 
                Chrome's local storage API (<code>chrome.storage.local</code>). No data is transmitted 
                to external servers.
              </li>
            </ul>
          </section>

          <section className="section">
            <h2>2. Data Usage</h2>
            <p>
              The collected data is used solely for the following purposes:
            </p>
            <ul>
              <li>Displaying your browsing time statistics in the extension popup</li>
              <li>Generating charts and visualizations of your browsing habits</li>
              <li>Providing insights into how you spend your time online</li>
            </ul>
            <p>
              <strong>We do NOT:</strong>
            </p>
            <ul>
              <li>Send any data to external servers</li>
              <li>Share your data with third parties</li>
              <li>Use your data for advertising or marketing</li>
              <li>Track you across different devices or browsers</li>
              <li>Collect personal information or personally identifiable information (PII)</li>
            </ul>
          </section>

          <section className="section">
            <h2>3. Permissions Explained</h2>
            <p>
              Web Time Insight requires the following Chrome permissions:
            </p>
            <ul>
              <li>
                <strong>tabs:</strong> Required to detect which tabs are active and track browsing time. 
                We only access tab information to determine active browsing time.
              </li>
              <li>
                <strong>windows:</strong> Required to detect when browser windows gain or lose focus, 
                ensuring we only track time when you're actively using the browser.
              </li>
              <li>
                <strong>storage:</strong> Required to save your browsing time data locally on your device.
              </li>
              <li>
                <strong>host_permissions (&lt;all_urls&gt;):</strong> Required to extract domain names 
                from URLs you visit. We only read URLs to extract domain information; we do not access 
                page content or any other data.
              </li>
            </ul>
          </section>

          <section className="section">
            <h2>4. Data Security</h2>
            <p>
              Your data security is important to us:
            </p>
            <ul>
              <li>All data is stored locally on your device using Chrome's secure storage API</li>
              <li>Data is never transmitted over the network</li>
              <li>No external servers or services are involved in data storage or processing</li>
              <li>You have full control over your data and can delete it at any time using the 
                  extension's reset features</li>
            </ul>
          </section>

          <section className="section">
            <h2>5. Data Retention and Deletion</h2>
            <p>
              <strong>Data Retention:</strong> Your browsing time data is retained indefinitely on your 
              device until you choose to delete it.
            </p>
            <p>
              <strong>Data Deletion:</strong> You can delete your data at any time:
            </p>
            <ul>
              <li>Use the "Reset Today" button to delete today's data</li>
              <li>Use the "Reset All Data" button to delete all stored data</li>
              <li>Uninstalling the extension will remove all associated data from Chrome's storage</li>
            </ul>
          </section>

          <section className="section">
            <h2>6. Third-Party Services</h2>
            <p>
              Web Time Insight does not use any third-party analytics, tracking, or advertising services. 
              The extension uses Chart.js (loaded via CDN) for data visualization, but this library does 
              not collect or transmit any data.
            </p>
          </section>

          <section className="section">
            <h2>7. Children's Privacy</h2>
            <p>
              Web Time Insight is not intended for use by children under the age of 13. We do not 
              knowingly collect data from children. If you are a parent or guardian and believe your 
              child has provided us with data, please contact us to have that data removed.
            </p>
          </section>

          <section className="section">
            <h2>8. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this 
              page with an updated "Last Updated" date. We encourage you to review this Privacy Policy 
              periodically to stay informed about how we protect your data.
            </p>
          </section>

          <section className="section">
            <h2>9. Your Rights</h2>
            <p>
              You have the following rights regarding your data:
            </p>
            <ul>
              <li><strong>Access:</strong> You can view all your data through the extension popup</li>
              <li><strong>Deletion:</strong> You can delete your data at any time using the reset features</li>
              <li><strong>Control:</strong> You can uninstall the extension at any time, which will remove all data</li>
            </ul>
          </section>

          <section className="section">
            <h2>10. Contact Information</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, 
              please contact us through the Chrome Web Store listing or the extension's support channels.
            </p>
          </section>

          <section className="section">
            <h2>11. Compliance</h2>
            <p>
              Web Time Insight is designed to comply with:
            </p>
            <ul>
              <li>Chrome Web Store Developer Program Policies</li>
              <li>General Data Protection Regulation (GDPR) principles</li>
              <li>California Consumer Privacy Act (CCPA) principles</li>
            </ul>
            <p>
              Since all data is stored locally and never transmitted externally, the extension operates 
              with minimal privacy risk and maximum user control.
            </p>
          </section>

          <footer className="footer">
            <p>
              <strong>Summary:</strong> Web Time Insight is a privacy-focused extension that stores 
              all data locally on your device. We do not collect, transmit, or share any data with 
              external parties. Your browsing time data belongs to you and stays on your device.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
